/**
 * Compact track owner picker (metadata-backed).
 * Used on track Overview (Participation card footer) and cycle Tracks list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserCircle, ChevronDown, X } from 'lucide-react';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  getTrackOwnerUserIdFromMetadata,
  mergeTrackOwnerIntoMetadata,
} from '@/types/prioritization/track';

export type TrackOwnerSubControlVariant = 'dark' | 'light' | 'surface';

export function TrackOwnerSubControl({
  trackId,
  variant = 'light',
  readOnly = false,
  workspaceId,
}: {
  trackId: string;
  variant?: TrackOwnerSubControlVariant;
  /** When true, show assigned owner only (no assign/change/clear). */
  readOnly?: boolean;
  workspaceId?: string | null;
}) {
  const track = useTrackStore((s) => s.tracks.find((t) => t.id === trackId));
  const updateTrack = useTrackStore((s) => s.updateTrack);
  const currentUserId = useUserStore((s) => s.userId);
  const sessionUserName = useUserStore((s) => s.userName);
  const sessionUserEmail = useUserStore((s) => s.userEmail);
  const { users, loading: usersLoading } = useSystemUsers(
    workspaceId ? { workspaceId } : undefined,
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  /** When owner id is set but not in workspace participant list, load profile (RLS permitting). */
  const [fetchedOwnerProfile, setFetchedOwnerProfile] = useState<{
    name: string | null;
    email: string | null;
  } | null>(null);
  const [ownerProfileLoading, setOwnerProfileLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const ownerId = track ? getTrackOwnerUserIdFromMetadata(track.metadata) : null;
  const ownerUser = ownerId ? users.find((u) => u.id === ownerId) : undefined;

  useEffect(() => {
    if (!ownerId || ownerUser || usersLoading) {
      setOwnerProfileLoading(false);
      if (!ownerId || ownerUser) setFetchedOwnerProfile(null);
      return;
    }
    if (ownerId === currentUserId) {
      setOwnerProfileLoading(false);
      setFetchedOwnerProfile(null);
      return;
    }
    let cancelled = false;
    setOwnerProfileLoading(true);
    setFetchedOwnerProfile(null);
    const run = async () => {
      const { getSafeSupabaseClient } = await import('@/lib/supabaseSync');
      const client = getSafeSupabaseClient();
      if (!client || cancelled) {
        if (!cancelled) setOwnerProfileLoading(false);
        return;
      }
      const { data, error } = await client
        .from('users')
        .select('id, name, email')
        .eq('id', ownerId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setOwnerProfileLoading(false);
        return;
      }
      const row = data as { name: string | null; email: string | null };
      setFetchedOwnerProfile({
        name: row.name,
        email: row.email,
      });
      setOwnerProfileLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
      setOwnerProfileLoading(false);
    };
  }, [ownerId, ownerUser, usersLoading, currentUserId]);

  const ownerLabel = useMemo(() => {
    if (!ownerId) return null;
    if (ownerUser?.name?.trim()) return ownerUser.name.trim();
    if (ownerUser?.email?.trim()) return ownerUser.email.trim();
    if (ownerId === currentUserId) {
      const n = sessionUserName?.trim();
      const e = sessionUserEmail?.trim();
      if (n) return n;
      if (e) return e;
      return 'You';
    }
    if (fetchedOwnerProfile?.name?.trim()) return fetchedOwnerProfile.name.trim();
    if (fetchedOwnerProfile?.email?.trim()) return fetchedOwnerProfile.email.trim();
    if (usersLoading || ownerProfileLoading) return 'Loading…';
    return 'Unknown user';
  }, [
    ownerId,
    ownerUser,
    currentUserId,
    sessionUserName,
    sessionUserEmail,
    fetchedOwnerProfile,
    usersLoading,
    ownerProfileLoading,
  ]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const persistOwner = useCallback(
    async (nextOwnerId: string | null) => {
      if (!track) return;
      setSaving(true);
      const merged = mergeTrackOwnerIntoMetadata(track.metadata, nextOwnerId);
      await updateTrack(trackId, { metadata: merged });
      setSaving(false);
      setOpen(false);
      setQuery('');
    },
    [track, trackId, updateTrack],
  );

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(query.toLowerCase()) ||
      u.email.toLowerCase().includes(query.toLowerCase()),
  );

  const isDark = variant === 'dark';
  const isSurface = variant === 'surface';
  const btnBase = isSurface
    ? 'text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] border-[var(--rule-bright)]'
    : isDark
    ? 'text-[var(--text-dim)] hover:text-white hover:bg-[var(--bg-raised)]/10 border-[var(--rule-bright)]'
    : 'text-[var(--text-sec)] hover:bg-[var(--surface)] border-[var(--rule)]';
  const popoverDark = isDark || isSurface;
  const popoverShell = popoverDark
    ? 'bg-[var(--surface)] border-[var(--rule-bright)] '
    : 'bg-[var(--bg-raised)] border-[var(--rule)] ';

  if (!track) return null;

  return (
    <div ref={rootRef} className="relative flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
      <div className={`flex items-center gap-1.5 min-w-0 ${isSurface ? 'text-[var(--text-ter)]' : isDark ? 'text-[var(--text-dim)]' : 'text-[var(--text-ter)]'}`}>
        <UserCircle className="h-3.5 w-3.5 shrink-0" />
        <span className="text-[11px] font-semibold uppercase tracking-wide shrink-0">Owner</span>
      </div>
      <span
        className={`text-xs font-medium truncate max-w-[140px] ${isSurface ? 'text-[var(--text-pri)]' : isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}
        title={ownerLabel ?? undefined}
      >
        {ownerLabel ?? 'Unassigned'}
      </span>
      {!readOnly && (
        <>
          <button
            type="button"
            disabled={saving}
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${btnBase}`}
          >
            {saving ? 'Saving…' : ownerId ? 'Change' : 'Assign'}
            <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {ownerId && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void persistOwner(null)}
              className={`text-[11px] font-medium px-1.5 py-0.5 ${isSurface ? 'text-[var(--mustard)] hover:bg-[var(--hover-tint)]' : isDark ? 'text-[var(--mustard)]/90 hover:bg-[var(--bg-raised)]/10' : 'text-[var(--mustard)] hover:bg-[var(--mustard-dim)]'}`}
            >
              Clear
            </button>
          )}
        </>
      )}

      {!readOnly && open && (
        <div
          className={`absolute z-50 top-full left-0 mt-1 w-72 border overflow-hidden ${popoverShell}`}
        >
          <div
            className={`px-3 py-2 border-b flex items-center justify-between ${popoverDark ? 'border-[var(--rule)]' : 'border-[var(--rule)]'}`}
          >
            <span className={`text-xs font-semibold uppercase tracking-wide ${popoverDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              Track owner
            </span>
            <button
              type="button"
              onClick={() => { setOpen(false); setQuery(''); }}
              className={popoverDark ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)]' : 'text-[var(--text-dim)] hover:text-[var(--text-ter)]'}
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className={`px-2 py-1.5 border-b ${popoverDark ? 'border-[var(--rule)]' : 'border-[var(--rule)]'}`}>
            <input
              autoFocus
              type="text"
              placeholder="Search name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={
                popoverDark
                  ? 'w-full text-sm px-2 py-1 border border-[var(--rule-bright)] bg-[var(--bg)] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-1 focus:ring-primary-500/60'
                  : 'w-full text-sm px-2 py-1 border border-[var(--rule)] focus:outline-none focus:ring-1 focus:ring-primary-400'
              }
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {usersLoading ? (
              <p className={`text-xs px-3 py-2 ${popoverDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-dim)]'}`}>Loading members…</p>
            ) : filtered.length === 0 ? (
              <p className={`text-xs px-3 py-2 ${popoverDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-dim)]'}`}>No members found.</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => void persistOwner(u.id)}
                  className={
                    popoverDark
                      ? 'w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--hover-tint)]'
                      : 'w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--surface)]'
                  }
                >
                  <div
                    className={`h-6 w-6 flex items-center justify-center shrink-0 text-[10px] font-semibold ${
                      popoverDark ? 'bg-primary-950/50 text-primary-200' : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${popoverDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}>
                      {u.name || '—'}
                    </p>
                    <p className={`text-xs truncate ${popoverDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-dim)]'}`}>{u.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
